import React, {useEffect, useState} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Paper,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {MetricsPreviewResponse, MetricsPreviewRow, MetricsStats, Page, SyncTarget} from '../types';
import {previewSyncTarget} from '../api/dataApi';

export type PreviewMetricsDialogProps = {
  open: boolean;
  target: SyncTarget | null;
  onClose: () => void;
};

export default function PreviewMetricsDialog(props: PreviewMetricsDialogProps) {
  const { open, target, onClose } = props;
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [data, setData] = useState<Page<MetricsPreviewRow> | null>(null);
  const [stats, setStats] = useState<MetricsStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && target) {
      setPage(0);
      setSize(10);
      setData(null);
      setStats(null);
      setError(null);
    }
  }, [open, target?.id]);

  async function load(p = page, s = size) {
    if (!target) return;
    setLoading(true);
    setError(null);
    try {
      const res = await previewSyncTarget(target.id, p, s);
      setData(res.data);
      setStats(res.stats);
      setPage(res.data.number);
      setSize(res.data.size);
    } catch (e) {
      setError('Failed to load preview');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && target) {
      load(0, size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.id]);

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).format(d);
    } catch {
      return iso as string;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        Preview data {target ? `for ${target.name}` : ''}
        {stats && (
          <Box sx={{ position: 'absolute', right: 48, top: 8, textAlign: 'right', mr: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
              Range: {formatDate(stats.minTimestamp || undefined)} - {formatDate(stats.maxTimestamp || undefined)}
            </Typography>
            <Tooltip title={
              <Box sx={{ p: 0.5 }}>
                {Object.entries(stats.tagSummary).map(([k, v]) => (
                  <Typography key={k} variant="caption" display="block">
                    <strong>{k}</strong>: {v.join(', ')}
                  </Typography>
                ))}
              </Box>
            }>
              <Typography variant="caption" color="primary" sx={{ cursor: 'help' }}>
                Tags: {Object.keys(stats.tagSummary).join(', ')}
              </Typography>
            </Tooltip>
          </Box>
        )}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!target && <Typography variant="body2">No target selected</Typography>}
        {target && (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Tags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!data || data.content.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2">{loading ? 'Loading...' : 'No data'}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.content.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatDate(row.timestamp)}</TableCell>
                        <TableCell>{row.value}</TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 640 }}>
                            <Tooltip title={row.tagsJson} placement="top-start">
                              <Typography variant="body2" noWrap>{row.tagsJson}</Typography>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data?.totalElements || 0}
              page={page}
              onPageChange={(_, newPage) => {
                const p = newPage;
                setPage(p);
                load(p, size);
              }}
              rowsPerPage={size}
              onRowsPerPageChange={(e) => {
                const s = parseInt(e.target.value, 10);
                setSize(s);
                load(0, s);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
      </DialogActions>
    </Dialog>
  );
}
